import { useCallback, useMemo, useState } from "react";
import {
  collectDescendantNodeIds,
  deriveAutoCollapsedNodes,
  normalizeLocationKeyForNode,
  type TreeIndex,
} from "../data/tree-navigation-state";
import type { TreeExpandState } from "../types";

interface TreeExpandControllerState {
  focusedNodeIdByRoot: Map<string, string>;
  manualCollapsedNodesByRoot: Map<string, Set<string>>;
  manualExpandedNodesByRoot: Map<string, Set<string>>;
  activeNodeId: string | null;
  activatedLocationKey: string | null;
  lastSyncedLocationKey: string | null;
  scrollRequest: TreeExpandState["scrollRequest"];
  scrollNonce: number;
}

const EMPTY_NODE_IDS = new Set<string>();

function getTopLevelRootId(treeIndex: TreeIndex, nodeId: string | null): string | null {
  if (!nodeId) {
    return null;
  }

  return treeIndex.topLevelRootIdById.get(nodeId) ?? null;
}

function getScopedNodeIds(
  scopedNodeIdsByRoot: Map<string, Set<string>>,
  rootId: string | null
): Set<string> {
  if (!rootId) {
    return EMPTY_NODE_IDS;
  }

  return scopedNodeIdsByRoot.get(rootId) ?? EMPTY_NODE_IDS;
}

function setScopedNodeIds(
  scopedNodeIdsByRoot: Map<string, Set<string>>,
  rootId: string | null,
  nodeIds: Set<string>
): Map<string, Set<string>> {
  if (!rootId) {
    return scopedNodeIdsByRoot;
  }

  const nextScopedNodeIdsByRoot = new Map(scopedNodeIdsByRoot);

  if (nodeIds.size === 0) {
    nextScopedNodeIdsByRoot.delete(rootId);
  } else {
    nextScopedNodeIdsByRoot.set(rootId, nodeIds);
  }

  return nextScopedNodeIdsByRoot;
}

function flattenScopedNodeIds(scopedNodeIdsByRoot: Map<string, Set<string>>): Set<string> {
  const nodeIds = new Set<string>();

  scopedNodeIdsByRoot.forEach((scopedNodeIds) => {
    scopedNodeIds.forEach((nodeId) => {
      nodeIds.add(nodeId);
    });
  });

  return nodeIds;
}

function pruneManualCollapsedNodesOnReentry(
  treeIndex: TreeIndex,
  manualCollapsedNodes: Set<string>,
  targetNodeId: string | null
): Set<string> {
  if (!targetNodeId || manualCollapsedNodes.size === 0) {
    return manualCollapsedNodes;
  }

  const targetLineage = treeIndex.lineageById.get(targetNodeId);
  if (!targetLineage || targetLineage.length === 0) {
    return manualCollapsedNodes;
  }

  let didPrune = false;
  const nextManualCollapsedNodes = new Set<string>();

  manualCollapsedNodes.forEach((nodeId) => {
    if (targetLineage.includes(nodeId)) {
      didPrune = true;
      return;
    }

    nextManualCollapsedNodes.add(nodeId);
  });

  return didPrune ? nextManualCollapsedNodes : manualCollapsedNodes;
}

function isDescendantSyncOfActivatedNode(
  treeIndex: TreeIndex,
  activatedLocationKey: string | null,
  activatedNodeId: string | null,
  normalizedLocationKey: string,
  targetNodeId: string | null
): boolean {
  if (!activatedLocationKey || !activatedNodeId || !targetNodeId) {
    return false;
  }

  if (activatedNodeId === targetNodeId) {
    return false;
  }

  if (activatedLocationKey === normalizedLocationKey) {
    return false;
  }

  const targetLineage = treeIndex.lineageById.get(targetNodeId) ?? [];
  return targetLineage.includes(activatedNodeId);
}

function isAncestorSyncOfActivatedNode(
  treeIndex: TreeIndex,
  activatedLocationKey: string | null,
  activatedNodeId: string | null,
  targetNodeId: string | null
): boolean {
  if (!activatedLocationKey || !activatedNodeId || !targetNodeId) {
    return false;
  }

  if (activatedNodeId === targetNodeId) {
    return false;
  }

  const activatedLineage = treeIndex.lineageById.get(activatedNodeId) ?? [];
  return activatedLineage.includes(targetNodeId);
}

export function useTreeExpandState(treeIndex: TreeIndex): TreeExpandState {
  const [state, setState] = useState<TreeExpandControllerState>(() => ({
    focusedNodeIdByRoot: new Map(),
    manualCollapsedNodesByRoot: new Map(),
    manualExpandedNodesByRoot: new Map(),
    activeNodeId: null,
    activatedLocationKey: null,
    lastSyncedLocationKey: null,
    scrollRequest: null,
    scrollNonce: 0,
  }));

  const activeLineageNodes = useMemo(() => {
    if (!state.activeNodeId) {
      return new Set<string>();
    }

    const lineage = treeIndex.lineageById.get(state.activeNodeId) ?? [];
    return new Set(lineage);
  }, [state.activeNodeId, treeIndex.lineageById]);

  const focusedLineageNodeIdsByRoot = useMemo(() => {
    const nextFocusedLineageNodeIdsByRoot = new Map<string, Set<string>>();

    state.focusedNodeIdByRoot.forEach((focusedNodeId, rootId) => {
      const lineage = treeIndex.lineageById.get(focusedNodeId);
      if (!lineage || lineage.length === 0) {
        return;
      }

      nextFocusedLineageNodeIdsByRoot.set(rootId, new Set(lineage));
    });

    return nextFocusedLineageNodeIdsByRoot;
  }, [state.focusedNodeIdByRoot, treeIndex.lineageById]);

  const autoCollapsedNodeIdsByRoot = useMemo(() => {
    const nextAutoCollapsedNodeIdsByRoot = new Map<string, Set<string>>();

    state.focusedNodeIdByRoot.forEach((focusedNodeId, rootId) => {
      const manualExpandedNodeIds = getScopedNodeIds(state.manualExpandedNodesByRoot, rootId);
      nextAutoCollapsedNodeIdsByRoot.set(
        rootId,
        deriveAutoCollapsedNodes(treeIndex, focusedNodeId, manualExpandedNodeIds)
      );
    });

    return nextAutoCollapsedNodeIdsByRoot;
  }, [treeIndex, state.focusedNodeIdByRoot, state.manualExpandedNodesByRoot]);

  const manualCollapsedNodes = useMemo(
    () => flattenScopedNodeIds(state.manualCollapsedNodesByRoot),
    [state.manualCollapsedNodesByRoot]
  );

  const manualExpandedNodes = useMemo(
    () => flattenScopedNodeIds(state.manualExpandedNodesByRoot),
    [state.manualExpandedNodesByRoot]
  );

  const isNodeExpanded = useCallback(
    (nodeId: string, defaultOpen: boolean): boolean => {
      const rootId = getTopLevelRootId(treeIndex, nodeId);
      const rootManualExpandedNodeIds = getScopedNodeIds(state.manualExpandedNodesByRoot, rootId);
      if (rootManualExpandedNodeIds.has(nodeId)) {
        return true;
      }

      const rootManualCollapsedNodeIds = getScopedNodeIds(state.manualCollapsedNodesByRoot, rootId);
      if (rootManualCollapsedNodeIds.has(nodeId)) {
        return false;
      }

      const rootFocusedLineageNodeIds = focusedLineageNodeIdsByRoot.get(rootId ?? "");
      if (rootFocusedLineageNodeIds?.has(nodeId)) {
        return true;
      }

      const rootAutoCollapsedNodeIds = autoCollapsedNodeIdsByRoot.get(rootId ?? "");
      if (rootAutoCollapsedNodeIds?.has(nodeId)) {
        return false;
      }

      return defaultOpen;
    },
    [
      autoCollapsedNodeIdsByRoot,
      focusedLineageNodeIdsByRoot,
      state.manualCollapsedNodesByRoot,
      state.manualExpandedNodesByRoot,
      treeIndex,
    ]
  );

  const expandManualNode = useCallback(
    (nodeId: string) => {
      setState((prev) => {
        const rootId = getTopLevelRootId(treeIndex, nodeId);
        const nextManualCollapsedNodes = new Set(
          getScopedNodeIds(prev.manualCollapsedNodesByRoot, rootId)
        );
        const nextManualExpandedNodes = new Set(
          getScopedNodeIds(prev.manualExpandedNodesByRoot, rootId)
        );
        nextManualCollapsedNodes.delete(nodeId);
        nextManualExpandedNodes.add(nodeId);

        return {
          ...prev,
          manualCollapsedNodesByRoot: setScopedNodeIds(
            prev.manualCollapsedNodesByRoot,
            rootId,
            nextManualCollapsedNodes
          ),
          manualExpandedNodesByRoot: setScopedNodeIds(
            prev.manualExpandedNodesByRoot,
            rootId,
            nextManualExpandedNodes
          ),
        };
      });
    },
    [treeIndex]
  );

  const collapseManualNode = useCallback(
    (nodeId: string) => {
      setState((prev) => {
        const rootId = getTopLevelRootId(treeIndex, nodeId);
        const descendantNodeIds = collectDescendantNodeIds(treeIndex, nodeId);
        const nextManualCollapsedNodes = new Set(
          getScopedNodeIds(prev.manualCollapsedNodesByRoot, rootId)
        );
        const nextManualExpandedNodes = new Set(
          getScopedNodeIds(prev.manualExpandedNodesByRoot, rootId)
        );

        nextManualCollapsedNodes.add(nodeId);
        nextManualExpandedNodes.delete(nodeId);

        descendantNodeIds.forEach((descendantNodeId) => {
          nextManualCollapsedNodes.delete(descendantNodeId);
          nextManualExpandedNodes.delete(descendantNodeId);
        });

        return {
          ...prev,
          manualCollapsedNodesByRoot: setScopedNodeIds(
            prev.manualCollapsedNodesByRoot,
            rootId,
            nextManualCollapsedNodes
          ),
          manualExpandedNodesByRoot: setScopedNodeIds(
            prev.manualExpandedNodesByRoot,
            rootId,
            nextManualExpandedNodes
          ),
        };
      });
    },
    [treeIndex]
  );

  const toggleManualNode = useCallback(
    (nodeId: string, defaultOpen: boolean) => {
      if (isNodeExpanded(nodeId, defaultOpen)) {
        collapseManualNode(nodeId);
      } else {
        expandManualNode(nodeId);
      }
    },
    [collapseManualNode, expandManualNode, isNodeExpanded]
  );

  const activateNode = useCallback(
    (nodeId: string, locationKey: string) => {
      setState((prev) => {
        const rootId = getTopLevelRootId(treeIndex, nodeId);
        const normalizedLocationKey = normalizeLocationKeyForNode(treeIndex, locationKey, nodeId);
        const nextManualCollapsedNodes = pruneManualCollapsedNodesOnReentry(
          treeIndex,
          getScopedNodeIds(prev.manualCollapsedNodesByRoot, rootId),
          nodeId
        );
        const nextFocusedNodeIdByRoot = new Map(prev.focusedNodeIdByRoot);
        if (rootId) {
          nextFocusedNodeIdByRoot.set(rootId, nodeId);
        }
        const scrollNonce = prev.scrollNonce + 1;

        return {
          ...prev,
          focusedNodeIdByRoot: nextFocusedNodeIdByRoot,
          manualCollapsedNodesByRoot: setScopedNodeIds(
            prev.manualCollapsedNodesByRoot,
            rootId,
            nextManualCollapsedNodes
          ),
          activeNodeId: nodeId,
          activatedLocationKey: normalizedLocationKey,
          scrollNonce,
          scrollRequest: {
            nodeId,
            nonce: scrollNonce,
          },
        };
      });
    },
    [treeIndex]
  );

  const syncFromLocation = useCallback(
    (locationKey: string, targetNodeId: string | null) => {
      setState((prev) => {
        const normalizedLocationKey = normalizeLocationKeyForNode(
          treeIndex,
          locationKey,
          targetNodeId
        );
        const isMatchingActivation =
          prev.activatedLocationKey === normalizedLocationKey && prev.activeNodeId === targetNodeId;
        const isMatchingActivationDescendant = isDescendantSyncOfActivatedNode(
          treeIndex,
          prev.activatedLocationKey,
          prev.activeNodeId,
          normalizedLocationKey,
          targetNodeId
        );
        const isPendingActivationAncestorSync = isAncestorSyncOfActivatedNode(
          treeIndex,
          prev.activatedLocationKey,
          prev.activeNodeId,
          targetNodeId
        );
        const isDuplicateSync =
          prev.lastSyncedLocationKey === normalizedLocationKey &&
          prev.activeNodeId === targetNodeId;

        if (isDuplicateSync) {
          return prev;
        }

        const isPendingActivationResolution =
          targetNodeId === null && prev.activatedLocationKey === normalizedLocationKey;
        if (isPendingActivationResolution || isPendingActivationAncestorSync) {
          return prev;
        }

        if (isMatchingActivation || isMatchingActivationDescendant) {
          const targetTopLevelRootId = getTopLevelRootId(treeIndex, targetNodeId);
          const nextFocusedNodeIdByRoot = new Map(prev.focusedNodeIdByRoot);
          if (targetTopLevelRootId && targetNodeId) {
            nextFocusedNodeIdByRoot.set(targetTopLevelRootId, targetNodeId);
          }

          return {
            ...prev,
            focusedNodeIdByRoot: nextFocusedNodeIdByRoot,
            activeNodeId: targetNodeId,
            activatedLocationKey: null,
            lastSyncedLocationKey: normalizedLocationKey,
          };
        }

        const targetTopLevelRootId = getTopLevelRootId(treeIndex, targetNodeId);
        const nextFocusedNodeIdByRoot = new Map(prev.focusedNodeIdByRoot);
        if (targetTopLevelRootId && targetNodeId) {
          nextFocusedNodeIdByRoot.set(targetTopLevelRootId, targetNodeId);
        }

        const scrollNonce = targetNodeId ? prev.scrollNonce + 1 : prev.scrollNonce;

        return {
          focusedNodeIdByRoot: nextFocusedNodeIdByRoot,
          manualCollapsedNodesByRoot: targetTopLevelRootId
            ? setScopedNodeIds(prev.manualCollapsedNodesByRoot, targetTopLevelRootId, new Set())
            : prev.manualCollapsedNodesByRoot,
          manualExpandedNodesByRoot: targetTopLevelRootId
            ? setScopedNodeIds(prev.manualExpandedNodesByRoot, targetTopLevelRootId, new Set())
            : prev.manualExpandedNodesByRoot,
          activeNodeId: targetNodeId,
          activatedLocationKey: null,
          lastSyncedLocationKey: normalizedLocationKey,
          scrollNonce,
          scrollRequest: targetNodeId
            ? {
                nodeId: targetNodeId,
                nonce: scrollNonce,
              }
            : null,
        };
      });
    },
    [treeIndex]
  );

  return {
    isNodeExpanded,
    toggleManualNode,
    expandManualNode,
    collapseManualNode,
    activateNode,
    syncFromLocation,
    manualCollapsedNodes,
    manualExpandedNodes,
    activeNodeId: state.activeNodeId,
    activeLineageNodeIds: activeLineageNodes,
    scrollRequest: state.scrollRequest,
  };
}
